// 雲台預置點（名稱 + 縮圖）——平台端點 /v1/homes/{home}/things/{device}/ptz/presets（plan SPEC #multi-lens-ptz、#multi-lens-presets），
// 照 component/kinio-app-core KinioPtzPresets.kt / .swift。
// 以**設備清單為主**：平台有資料的用名稱和縮圖，沒有的顯示「位置 n」、無縮圖、選單多〔命名〕（只寫平台）。名額「n / max」照設備清單。
// 單鏡頭的雲台機種同一套（舊韌體 list 沒有 max / epoch：平台回 max 8、epoch null）。
// 規則與案例：testdata/ptz_presets/kinio-ptz-presets.json（照抄 kinio-app-core docs/testdata）。

import { kinioFetch } from './auth';
import { planeBase } from './config';
import { KinioError } from './errors';

/** 舊韌體（list 沒有 max）的名額。 */
export const PTZ_PRESET_MAX_DEFAULT = 8;
/** 名稱最多幾個字素（平台 422 INVALID_NAME）。 */
export const PTZ_PRESET_NAME_MAX = 12;
/** goto 到位才回 ack，最多等這麼久（秒）。 */
export const PTZ_GOTO_WAIT_S = 20;
/** ack 之後超過這麼久收到 pan / tilt 位置變化 → 「目前所在」熄（ms）。 */
export const PTZ_HERE_SETTLE_MS = 2000;

export interface PtzPreset {
  id: number;
  /** 平台沒資料 = null（顯示「位置 {id}」，選單多〔命名〕）。 */
  name: string | null;
  thumbUrl: string | null;
  thumbLens: number | null;
  /** 平台資料的版本（改名帶 expected_version；平台沒資料 = null）。 */
  version: number | null;
  named: boolean;
}

export interface PtzPresetList {
  presets: PtzPreset[];
  max: number;
  epoch: number | null;
  /** 設備離線：平台回最後一次清單（新增 / 更新位置 / 刪除要設備，改名可以）。 */
  deviceUnreachable: boolean;
  full: boolean;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

export function parsePtzPresetList(body: unknown): PtzPresetList {
  const o = isObj(body) ? body : {};
  const presets: PtzPreset[] = [];
  if (Array.isArray(o.presets)) {
    for (const p of o.presets) {
      if (!isObj(p)) continue;
      const id = typeof p.id === 'number' ? Math.trunc(p.id) : 0;
      if (id < 1 || id > 255) continue;
      const name = typeof p.name === 'string' && p.name.trim() ? p.name : null;
      presets.push({
        id,
        name,
        thumbUrl: typeof p.thumb_url === 'string' && p.thumb_url ? p.thumb_url : null,
        thumbLens: typeof p.thumb_lens === 'number' ? p.thumb_lens : null,
        version: typeof p.version === 'number' ? p.version : null,
        named: name !== null,
      });
    }
  }
  presets.sort((a, b) => a.id - b.id);
  const max = Math.min(Math.max(typeof o.max === 'number' ? Math.trunc(o.max) : PTZ_PRESET_MAX_DEFAULT, 1), 255);
  return {
    presets,
    max,
    epoch: typeof o.epoch === 'number' ? o.epoch : null,
    deviceUnreachable: o.device_unreachable === true,
    full: presets.length >= max,
  };
}

/** 新增時名稱預填「位置 n」的 n：最小的沒用過的編號。 */
export function presetDefaultNameNumber(list: PtzPresetList): number {
  for (let n = 1; n <= 255; n++) if (!list.presets.some((p) => p.id === n)) return n;
  return 255;
}

/** 名稱合不合法（去頭尾空白後 1–12 個字素、沒有控制字元）；不合法 = null。 */
export function cleanPresetName(raw: string): string | null {
  const t = raw.trim();
  // eslint-disable-next-line no-control-regex
  if (!t || /[\u0000-\u001f\u007f-\u009f]/.test(t)) return null;
  const n = graphemeCount(t);
  return n >= 1 && n <= PTZ_PRESET_NAME_MAX ? t : null;
}

function graphemeCount(s: string): number {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: string }) => { segment(s: string): Iterable<unknown> } }).Segmenter;
  if (Seg) {
    let n = 0;
    for (const _ of new Seg(undefined, { granularity: 'grapheme' }).segment(s)) n++;
    return n;
  }
  return Array.from(s).length;
}

/**
 * 「目前所在」：只在這支手機這次 goto 成功後亮；送 jog / 另一個 goto、或 ack 後超過 2 秒收到 pan / tilt 位置變化就熄；重新進頁不亮。
 */
export class PresetHere {
  here: number | null = null;
  private ackAt = 0;

  gotoAcked(id: number, now: number): void {
    this.here = id;
    this.ackAt = now;
  }

  /** 送 jog / 移動 / 另一個 goto。 */
  moved(): void {
    this.here = null;
  }

  /** 收到 pan / tilt 位置變化。 */
  positionChanged(now: number): void {
    if (this.here !== null && now - this.ackAt > PTZ_HERE_SETTLE_MS) this.here = null;
  }
}

export type PresetErrorKind = 'BUSY' | 'NOT_FOUND' | 'NOT_CALIBRATED' | 'PREEMPTED' | 'OFFLINE' | 'ASLEEP' | 'TIMEOUT' | 'LIMIT' | 'CONFLICT' | 'INVALID_NAME' | 'OTHER';

/** 平台錯誤碼（+ `detail.result` / `detail.reason`）→ 分類。`PREEMPTED` = 被新的 goto / jog 取代：UI 丟掉、不提示。 */
export function presetErrorKind(code: string | null, result: string | null, reason: string | null): PresetErrorKind {
  if (code === 'PRESET_LIMIT') return 'LIMIT';
  if (code === 'VERSION_CONFLICT') return 'CONFLICT';
  if (code === 'INVALID_NAME') return 'INVALID_NAME';
  if (code === 'DEVICE_OFFLINE') return 'OFFLINE';
  if (code === 'DEVICE_ASLEEP' || code === 'DEVICE_WAKING') return 'ASLEEP';
  if (code === 'COMMAND_TIMEOUT') return 'TIMEOUT';
  if (reason === 'preempted') return 'PREEMPTED';
  if (reason === 'not_found') return 'NOT_FOUND';
  if (reason === 'not_calibrated') return 'NOT_CALIBRATED';
  if (result === 'DEVICE_BUSY' || code === 'DEVICE_BUSY') return 'BUSY';
  return 'OTHER';
}

/** 任意錯誤 → 分類（KinioError 看 code / detail）。 */
export function presetErrorOf(e: unknown): PresetErrorKind {
  if (!(e instanceof KinioError)) return 'OTHER';
  const d = isObj(e.detail) ? e.detail : {};
  const s = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return presetErrorKind(e.code, s(d.result), s(d.reason));
}

// ── 端點 ──

function presetsUrl(apiEndpoint: string, homeId: string, device: string, tail = ''): string {
  return `${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}/ptz/presets${tail}`;
}

/** GET …/ptz/presets。 */
export async function listPtzPresets(apiEndpoint: string, homeId: string, device: string, signal?: AbortSignal): Promise<PtzPresetList> {
  return parsePtzPresetList(await kinioFetch<unknown>(presetsUrl(apiEndpoint, homeId, device), { signal }));
}

/** POST …/ptz/presets {name}：平台挑號、下 set_preset（存目前位置）→ 新的預置點。 */
export async function createPtzPreset(apiEndpoint: string, homeId: string, device: string, name: string): Promise<{ id: number; name: string | null; version: number | null }> {
  const o = await kinioFetch<Record<string, unknown>>(presetsUrl(apiEndpoint, homeId, device), { method: 'POST', body: { name } });
  return { id: Number(o?.id ?? 0), name: typeof o?.name === 'string' ? o.name : null, version: typeof o?.version === 'number' ? o.version : null };
}

/** PUT …/ptz/presets/{id} {name, expected_version?}：命名 / 改名只動平台（設備離線也行）。 */
export async function renamePtzPreset(apiEndpoint: string, homeId: string, device: string, id: number, name: string, expectedVersion?: number | null): Promise<void> {
  await kinioFetch(presetsUrl(apiEndpoint, homeId, device, `/${id}`), {
    method: 'PUT',
    body: { name, ...(expectedVersion != null ? { expected_version: expectedVersion } : {}) },
  });
}

/** POST …/ptz/presets/{id}/update-position：用目前位置更新（要設備）。 */
export async function updatePtzPresetPosition(apiEndpoint: string, homeId: string, device: string, id: number): Promise<void> {
  await kinioFetch(presetsUrl(apiEndpoint, homeId, device, `/${id}/update-position`), { method: 'POST', body: {} });
}

/** DELETE …/ptz/presets/{id}：設備 clear_preset（not_found 也算刪掉）+ 刪平台資料。 */
export async function deletePtzPreset(apiEndpoint: string, homeId: string, device: string, id: number): Promise<void> {
  await kinioFetch(presetsUrl(apiEndpoint, homeId, device, `/${id}`), { method: 'DELETE' });
}

/** PUT …/ptz/presets/{id}/thumbnail：JPEG（長邊 ≤ 320、≤ 32 KB），記拍的是哪顆鏡頭。 */
export async function putPtzPresetThumbnail(apiEndpoint: string, homeId: string, device: string, id: number, jpeg: Blob, lens?: number): Promise<void> {
  const q = lens !== undefined ? `?lens=${lens}` : '';
  await kinioFetch(presetsUrl(apiEndpoint, homeId, device, `/${id}/thumbnail${q}`), { method: 'PUT', body: jpeg, headers: { 'Content-Type': 'image/jpeg' } });
}

/**
 * 轉到預置點：命令 `kinio_ptz.goto_preset {id}`（設備到位才回 ack，最長 20 秒）。設備拒絕（`REJECTED preempted` 等）→
 * KinioError（code = 設備結果、detail.reason = 原因），用 presetErrorOf 分類。
 */
export async function gotoPtzPreset(apiEndpoint: string, homeId: string, device: string, id: number): Promise<void> {
  const url = `${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}/commands`;
  const o = await kinioFetch<{ status?: string; result?: { status?: string; reason?: string } }>(url, {
    method: 'POST',
    body: { op: 'set', id: 'kinio_ptz.goto_preset', payload: { id }, wait_seconds: PTZ_GOTO_WAIT_S },
  });
  // 設備明確回失敗才算失敗（沒帶 status 當成功；pending = 送出未等 ack，同 app-core KinioCommandResult.failed）
  const st = o?.status;
  if (!st || st === 'acked' || st === 'pending') return;
  const result = (typeof o?.result?.status === 'string' && o.result.status) || st;
  const reason = typeof o?.result?.reason === 'string' && o.result.reason ? o.result.reason : null;
  throw new KinioError(502, 'DEVICE_REJECTED', `goto_preset ${result}`, { result, ...(reason ? { reason } : {}) });
}
